import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Button,
  Divider,
  InputAdornment,
  CircularProgress,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Clear as ClearIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import salesService from '../../services/salesService';
import CustomerForm from './CustomerForm';

/**
 * Component to select customers for sales orders
 * @param {Object} props Component props
 * @param {Function} props.onSelectCustomer Callback when a customer is selected
 * @param {Object} props.selectedCustomer Currently selected customer (optional)
 * @returns {JSX.Element} Customer selector component
 */
const CustomerSelector = ({ onSelectCustomer, selectedCustomer }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load customers on mount and when search query changes
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true);
        // Use a large page size to get all customers for POS selection
        const response = await salesService.getAllCustomers({
          page: 0,
          size: 1000, // Large enough to get all customers
          sort: 'name',
          direction: 'ASC'
        });
        
        // Handle both paginated and non-paginated responses
        if (response.content) {
          setCustomers(response.content);
        } else if (Array.isArray(response)) {
          setCustomers(response);
        } else {
          setCustomers([]);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Filter customers based on search query
  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle new customer form success
  const handleCustomerFormSuccess = (newCustomer, error) => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    } else {
      // Add the new customer to the list
      setCustomers([...customers, newCustomer]);
      
      // Close the dialog
      setAddCustomerDialogOpen(false);
      
      // Select the new customer
      onSelectCustomer(newCustomer);
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Customer "${newCustomer.name}" added successfully and selected`,
        severity: 'success'
      });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Search field */}
      <TextField
        fullWidth
        placeholder="Search customers..."
        variant="outlined"
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearSearch}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* New customer button */}
      <Button
        variant="outlined"
        startIcon={<PersonAddIcon />}
        onClick={() => setAddCustomerDialogOpen(true)}
        sx={{ mb: 2 }}
        fullWidth
      >
        Add New Customer
      </Button>

      {/* Customers list */}
      <Typography variant="subtitle2" gutterBottom>
        Select a customer
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : filteredCustomers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            No customers found
          </Typography>
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
          <List disablePadding>
            {filteredCustomers.map((customer) => (
              <React.Fragment key={customer.id}>
                <ListItem
                  button
                  selected={selectedCustomer && selectedCustomer.id === customer.id}
                  onClick={() => onSelectCustomer(customer)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={customer.name}
                    secondary={
                      <React.Fragment>
                        {customer.email && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ display: 'block' }}
                          >
                            {customer.email}
                          </Typography>
                        )}
                        {customer.phone && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ display: 'block' }}
                          >
                            {customer.phone}
                          </Typography>
                        )}
                      </React.Fragment>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Add Customer Dialog */}
      <Dialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New Customer
          <IconButton
            aria-label="close"
            onClick={() => setAddCustomerDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <CustomerForm
          onSuccess={handleCustomerFormSuccess}
          onCancel={() => setAddCustomerDialogOpen(false)}
        />
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerSelector; 